'use client';

import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useWorkspaceDetection } from '@/hooks/useWorkspaceDetection';
import { PATH } from '@/constants';
import { useRouter, useSearchParams } from 'next/navigation';
import clsx from 'clsx';
import {
  Activity,
  Bell,
  Bot,
  Building2,
  CreditCard,
  Globe,
  Layers,
  Link2,
  Mail,
  MailX,
  MessageSquareIcon,
  Plug,
  Radio,
  Search,
  Shield,
  ShieldCheck,
  ScrollText,
  Timer,
  ToggleLeft,
  Trophy,
  User,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { ElementType, FC, useMemo, useState } from 'react';
import { SETTINGS_TAB, SETTINGS_TAB_REDIRECTS } from '@/views/settings/components/SettingsMenu';
import { canAccessSettingsTab } from '@/views/settings/components/settingsTabAccess';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface SettingsSidebarMenuProps {
  isVisuallyExpanded?: boolean;
  onCloseSidebar?: () => void;
}

interface SettingsNavItem {
  value: SETTINGS_TAB;
  label: string;
  icon: ElementType;
}

interface SettingsNavSection {
  title: string;
  items: SettingsNavItem[];
}

const ACCOUNT_ITEMS: SettingsNavItem[] = [
  { value: SETTINGS_TAB.PROFILE, label: 'Profile', icon: User },
  { value: SETTINGS_TAB.SECURITY_AND_API, label: 'Security & API', icon: Shield },
];

const WORKSPACE_ITEMS: SettingsNavItem[] = [
  { value: SETTINGS_TAB.WORKSPACE, label: 'Workspace', icon: Building2 },
  { value: SETTINGS_TAB.TEAM_ACCESS, label: 'Team', icon: Users },
  { value: SETTINGS_TAB.ROLES_AND_PERMISSIONS, label: 'Roles & Permissions', icon: ShieldCheck },
  { value: SETTINGS_TAB.BILLING_AND_USAGE, label: 'Billing & Usage', icon: CreditCard },
  { value: SETTINGS_TAB.INTEGRATIONS, label: 'Integrations', icon: Plug },
];

const INTELLIGENCE_ITEMS: SettingsNavItem[] = [
  { value: SETTINGS_TAB.CHAMPIONS, label: 'Champion Tracking', icon: Trophy },
  { value: SETTINGS_TAB.SIGNALS_AND_RULES, label: 'Signals & Rules', icon: Zap },
];

const CHANNELS_ITEMS: SettingsNavItem[] = [
  { value: SETTINGS_TAB.OUTREACH, label: 'Channels', icon: Radio },
  { value: SETTINGS_TAB.NOTIFICATIONS, label: 'Notifications', icon: Bell },
  { value: SETTINGS_TAB.EMAIL_DIGEST, label: 'Email Digest', icon: Mail },
  { value: SETTINGS_TAB.EMAIL_UNSUBSCRIBES, label: 'Unsubscribes', icon: MailX },
];

const TECHNICAL_ITEMS: SettingsNavItem[] = [
  { value: SETTINGS_TAB.MB_INTENT_CLIENT, label: 'Website Embed', icon: Globe },
];

const SUPPORT_ITEMS: SettingsNavItem[] = [
  { value: SETTINGS_TAB.FEEDBACK, label: 'Feedback', icon: MessageSquareIcon },
];

const SUPER_ADMIN_ITEMS: SettingsNavItem[] = [
  { value: SETTINGS_TAB.SYSTEM_STATS, label: 'System Stats', icon: Activity },
  { value: SETTINGS_TAB.SYSTEM_JOBS, label: 'System Jobs', icon: Timer },
  { value: SETTINGS_TAB.FEATURE_ENABLEMENT, label: 'Feature Flags', icon: ToggleLeft },
  { value: SETTINGS_TAB.USER_MAPPING, label: 'User Mapping', icon: Link2 },
  { value: SETTINGS_TAB.SYSTEM_WORKFLOWS, label: 'System Workflows', icon: Layers },
  { value: SETTINGS_TAB.SYSTEM_AGENTS, label: 'System Agents', icon: Bot },
  { value: SETTINGS_TAB.SIGNALS_ADMIN, label: 'Signals Browser', icon: Radio },
  { value: SETTINGS_TAB.EMAIL_SIGNATURES_ADMIN, label: 'Email Signatures', icon: ScrollText },
];

const SettingsSidebarMenu: FC<SettingsSidebarMenuProps> = ({ isVisuallyExpanded, onCloseSidebar }) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const workspaceContext = useWorkspace();
  const { shouldShowOrganizationSettings } = useWorkspaceDetection();

  const rawTab = searchParams?.get('tab') as SETTINGS_TAB | null;
  const activeTab: SETTINGS_TAB =
    (rawTab && SETTINGS_TAB_REDIRECTS[rawTab as SETTINGS_TAB]) || rawTab || SETTINGS_TAB.INTEGRATIONS;

  const isSuperAdmin = useMemo(() => {
    if (!workspaceContext || workspaceContext.isLoading) return false;
    return workspaceContext.workspaces.some((workspace) => workspace.is_member === false);
  }, [workspaceContext]);

  const isEnterprise = workspaceContext?.isEnterprise ?? false;
  const productModules = workspaceContext?.activeWorkspace?.product_modules;

  // GH#1280: Unified module/tier gating via canAccessSettingsTab
  const accessContext = useMemo(
    () => ({ isEnterprise, productModules }),
    [isEnterprise, productModules],
  );

  const workspaceItems = useMemo(
    () => WORKSPACE_ITEMS.filter((i) => canAccessSettingsTab(i.value, accessContext)),
    [accessContext],
  );

  const intelligenceItems = useMemo(
    () => INTELLIGENCE_ITEMS.filter((i) => canAccessSettingsTab(i.value, accessContext)),
    [accessContext],
  );

  const channelsItems = useMemo(
    () => CHANNELS_ITEMS.filter((i) => canAccessSettingsTab(i.value, accessContext)),
    [accessContext],
  );

  const technicalItems = useMemo(
    () => TECHNICAL_ITEMS.filter((i) => canAccessSettingsTab(i.value, accessContext)),
    [accessContext],
  );

  const visibleSections = useMemo<SettingsNavSection[]>(() => {
    const sections: SettingsNavSection[] = [
      { title: 'Account', items: ACCOUNT_ITEMS },
    ];

    if (shouldShowOrganizationSettings) {
      sections.push(
        { title: 'Workspace', items: workspaceItems },
        { title: 'Intelligence', items: intelligenceItems },
        { title: 'Channels', items: channelsItems },
        { title: 'Technical', items: technicalItems },
      );
    }

    sections.push({ title: 'Support', items: SUPPORT_ITEMS });

    if (isSuperAdmin) {
      sections.push({ title: 'Super Admin', items: SUPER_ADMIN_ITEMS });
    }

    return sections.filter((section) => section.items.length > 0);
  }, [channelsItems, intelligenceItems, isSuperAdmin, shouldShowOrganizationSettings, technicalItems, workspaceItems]);

  const filteredSections = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
    if (!normalizedQuery) return visibleSections;

    return visibleSections
      .map((section) => {
        const sectionMatches = section.title.toLocaleLowerCase().includes(normalizedQuery);
        const items = sectionMatches
          ? section.items
          : section.items.filter((item) => (
              `${item.label} ${item.value.replaceAll('-', ' ')}`
                .toLocaleLowerCase()
                .includes(normalizedQuery)
            ));

        return { ...section, items };
      })
      .filter((section) => section.items.length > 0);
  }, [searchQuery, visibleSections]);

  const handleTabChange = (tab: SETTINGS_TAB) => {
    router.push(`${PATH.SETTINGS}?tab=${tab}`);
    onCloseSidebar?.();
  };

  const renderItem = (item: SettingsNavItem) => {
    const isActive = item.value === activeTab;
    const Icon = item.icon;

    const itemContent = (
      <button
        onClick={() => handleTabChange(item.value)}
        className={clsx('mbn-nav-link', isActive && 'mbn-current')}
        aria-current={isActive ? 'page' : undefined}
        aria-label={item.label}
      >
        <Icon
          className='shrink-0 pointer-events-none'
          color='currentColor'
          width={16}
          height={16}
          strokeWidth={1.5}
        />
        <span className={clsx('mbn-label', !isVisuallyExpanded && 'sr-only')}>
          {item.label}
        </span>
      </button>
    );

    if (!isVisuallyExpanded) {
      return (
        <TooltipProvider key={item.value}>
          <Tooltip>
            <TooltipTrigger asChild>{itemContent}</TooltipTrigger>
            <TooltipContent side='right'>
              <p>{item.label}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return <div key={item.value}>{itemContent}</div>;
  };

  const renderSection = (title: string, items: SettingsNavItem[], isFirst = false) => (
    <li key={title} className={isFirst ? undefined : 'mbn-group'}>
      <div className='mbn-group-label'>
        {isVisuallyExpanded ? (
          <span className='mbn-section-title'>{title}</span>
        ) : (
          <div className='w-full h-px bg-gray-200' />
        )}
      </div>
      <ul className='mbn-section-items'>
        {items.map((item) => (
          <li key={item.value}>{renderItem(item)}</li>
        ))}
      </ul>
    </li>
  );

  return (
    <nav aria-label='Settings navigation' className='mbn-nav'>
      {isVisuallyExpanded && (
        <form
          role='search'
          className='mbn-settings-search'
          onSubmit={(event) => event.preventDefault()}
        >
          <label className='relative block'>
            <span className='sr-only'>Search settings</span>
            <Search
              aria-hidden='true'
              className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none'
              width={18}
              height={18}
              strokeWidth={1.75}
            />
            <input
              type='search'
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder='Search settings'
              className='h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-8 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-500 focus:border-gray-400 focus:ring-2 focus:ring-gray-100 [&::-webkit-search-cancel-button]:hidden'
            />
            {searchQuery && (
              <button
                type='button'
                aria-label='Clear settings search'
                onClick={() => setSearchQuery('')}
                className='absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
              >
                <X aria-hidden='true' width={16} height={16} strokeWidth={1.75} />
              </button>
            )}
          </label>
        </form>
      )}
      <ul className='mbn-nav-list'>
        {filteredSections.map((section, index) => renderSection(section.title, section.items, index === 0))}
      </ul>
      {searchQuery.trim() && filteredSections.length === 0 && (
        <div role='status' className='px-6 py-8 text-center'>
          <p className='text-sm font-medium text-gray-900'>No settings found</p>
          <p className='mt-1 text-xs text-gray-500'>Try a different search.</p>
        </div>
      )}
    </nav>
  );
};

export default SettingsSidebarMenu;
